/**
 * A donde vuelve el comprador tras pagar en Webpay (PAG-02).
 *
 * La referencia viaja por query string porque la deja el backend al
 * redirigir desde `/api/pedidos/retorno`, no un paso mas del formulario del
 * checkout: sin ella no hay nada que mostrar, y se vuelve al carro, igual que
 * el checkout sin carro (misma logica, mismo lugar en el codigo).
 *
 * **Que se compro, en esta pantalla.** `GET /api/pedidos/:referencia` solo
 * devuelve referencia, total y documento — a proposito: la referencia es un
 * correlativo (`EC-2026-000123`), no un token secreto, y esta ruta es
 * publica. Meter ahi el nombre, el telefono o la direccion del comprador
 * dejaria esos datos a un clic de cualquiera que probara referencias
 * consecutivas.
 *
 * **El comprobante (PAG-04)** es la excepcion, y por eso pide algo mas que la
 * referencia: con el `token` de la transaccion en la URL —el que Webpay le
 * dejo a ESTE navegador— la API entrega ademas el codigo de autorizacion, el
 * tipo de pago, las cuotas, los ultimos digitos de la tarjeta y las lineas del
 * pedido. Transbank exige mostrarlos para certificar la integracion.
 *
 * **La lista de lo comprado sale de esas lineas, nunca del carro.** El carro
 * guardado en este navegador puede no tener nada que ver con lo que se pago
 * (otra pestana, un pedido anterior): en otra tienda con este mismo codigo una
 * compra de un remo mostro "2 cascos". Del carro se toman solo las fotos, por
 * codigo: si no estan (se recargo la pagina, se abrio en otro navegador), la
 * linea sale con su hueco de foto y el resto igual.
 *
 * El pago aprobado vacia el carro y borra el formulario de checkout guardado
 * (`almacen-checkout.ts`): la compra ya se cerro, y dejarlos armados
 * mostraria de nuevo, en la proxima visita, un pedido que ya se pago. Un
 * pago rechazado NO los borra a proposito: es lo que permite que "Volver al
 * checkout" encuentre el formulario tal como se dejo, en vez de en blanco.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';

import type { RespuestaConfirmacionPedido } from '@elcarpintero/compartido';

import { Comprobante } from '../componentes/checkout/Comprobante';
import { AvisoError, Cargando } from '../componentes/comunes/EstadoPeticion';
import { Icono } from '../componentes/comunes/Icono';
import { FotoProducto } from '../componentes/comunes/Imagen';
import { useCarro } from '../contexto/carro-contexto';
import { usePeticion } from '../hooks/use-peticion';
import { registrarCompra } from '../medicion/eventos';
import { Seo } from '../seo/Seo';
import { registrarClicWhatsapp } from '../servicios/metricas.servicio';
import { obtenerConfirmacionPedido } from '../servicios/pedido.servicio';
import { borrarCheckoutGuardado } from '../utilidades/almacen-checkout';
import { pesos } from '../utilidades/formato';
import { esPaquete, textoPaquete } from '../utilidades/paquete';
import { rutas } from '../utilidades/rutas';
import { whatsappConsultaPedido, whatsappPagoRechazado } from '../utilidades/whatsapp';

/** Cuanto dura el "Copiado" antes de volver a mostrar el boton normal. */
const COPIADO_MS = 1800;

export function ConfirmacionPedido() {
  const [parametros] = useSearchParams();
  const referencia = parametros.get('referencia');
  const { carro, vaciar } = useCarro();

  // Las fotos del carro tal como estaba al llegar, por codigo: `vaciar()` lo
  // deja en cero un instante despues. SOLO las fotos: que se compro, cuanto y
  // a que precio sale del pedido guardado, nunca de este carro.
  const [fotoPorCodigo] = useState(
    () => new Map(carro.items.map((item) => [item.producto.sku, item.producto.imagen])),
  );
  const carroYaVaciado = useRef(false);

  const [copiado, setCopiado] = useState(false);

  // El token de la transaccion, que el backend arrastra desde el retorno de
  // Webpay. Es lo que autoriza a esta pantalla a pedir el comprobante.
  const token = parametros.get('token');

  const resultado = usePeticion<RespuestaConfirmacionPedido | null>(
    (senal) =>
      referencia === null
        ? Promise.resolve(null)
        : obtenerConfirmacionPedido(referencia, token, senal),
    [referencia ?? '', token ?? ''],
  );

  useEffect(() => {
    if (resultado.datos?.aprobado === true && !carroYaVaciado.current) {
      carroYaVaciado.current = true;
      vaciar();
      // El pago se aprobo: ya no hay razon para seguir ofreciendo estos datos
      // en el proximo checkout (almacen-checkout.ts).
      borrarCheckoutGuardado();
      registrarCompra({
        referencia: resultado.datos.referencia,
        total: resultado.datos.total,
        items: resultado.datos.comprobante?.items ?? [],
      });
    }
  }, [resultado.datos, vaciar]);

  useEffect(() => {
    if (!copiado) return;
    const reloj = setTimeout(() => {
      setCopiado(false);
    }, COPIADO_MS);
    return () => clearTimeout(reloj);
  }, [copiado]);

  async function copiarReferencia(valor: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(valor);
      setCopiado(true);
    } catch {
      // Portapapeles bloqueado o sin permiso: el numero sigue visible para copiar a mano.
    }
  }

  // Sin referencia no hay pedido que mostrar. Va despues de los hooks: React
  // exige que se llamen siempre, en el mismo orden.
  if (referencia === null) return <Navigate to={rutas.carro()} replace />;

  const datos = resultado.datos;

  return (
    <>
      <Seo
        titulo="Confirmación de tu compra"
        descripcion="Resultado de tu pago con Webpay."
        ruta={rutas.confirmacionPedido(referencia)}
        sinIndexar
      />

      <div className="contenedor pagina">
        {resultado.estado === 'cargando' ? <Cargando que="el resultado de tu pago" /> : null}
        {resultado.estado === 'error' ? <AvisoError error={resultado.error} /> : null}

        {datos !== null && datos.aprobado ? (
          <section className="confirmacion confirmacion-exito" aria-labelledby="confirmacion-titulo">
            <div className="confirmacion-insignia" aria-hidden="true">
              <span className="confirmacion-onda" />
              <span className="confirmacion-onda confirmacion-onda-2" />
              <span className="confeti confeti-1" />
              <span className="confeti confeti-2" />
              <span className="confeti confeti-3" />
              <span className="confeti confeti-4" />
              <span className="confeti confeti-5" />
              <span className="confeti confeti-6" />
              <div className="confirmacion-check">
                <Icono nombre="check" />
              </div>
            </div>

            <h1 id="confirmacion-titulo">¡Listo, tu compra quedó confirmada!</h1>
            <p className="confirmacion-bajada">
              Recibimos tu pago sin problemas y ya estamos preparando tu pedido.
            </p>

            <div className="confirmacion-ticket">
              <div className="confirmacion-ticket-cab">
                <span>
                  <span className="k">Número de pedido</span>
                  <span className="mono">{datos.referencia}</span>
                </span>

                <button
                  type="button"
                  className="btn btn-xs btn-suave"
                  onClick={() => {
                    void copiarReferencia(datos.referencia);
                  }}
                >
                  <Icono nombre={copiado ? 'check' : 'copiar'} />
                  {copiado ? 'Copiado' : 'Copiar'}
                </button>
              </div>

              {datos.comprobante !== null ? (
                <ul className="confirmacion-items">
                  {datos.comprobante.items.map((item) => (
                    <li key={item.id}>
                      <span className="confirmacion-foto">
                        <FotoProducto imagen={fotoPorCodigo.get(item.sku) ?? null} nombre={item.nombre} />
                      </span>
                      <span className="confirmacion-item-datos">
                        <span className="n">{item.nombre}</span>
                        <span className="d">
                          {item.cantidad} × {pesos(item.precioUnitario)}
                          {esPaquete(item) ? ` · ${textoPaquete(item)}` : null}
                        </span>
                      </span>
                      <span className="confirmacion-item-monto">{pesos(item.subtotal)}</span>
                    </li>
                  ))}
                </ul>
              ) : null}

              <p className="confirmacion-fila">
                <span className="k">Documento tributario</span>
                <span>
                  {datos.folioDocumento === null
                    ? 'Se está emitiendo, te llega por correo'
                    : datos.documentoEnviado
                      ? `Folio ${datos.folioDocumento}, enviado a tu correo`
                      : `Folio ${datos.folioDocumento}`}
                </span>
              </p>

              <p className="confirmacion-fila total">
                <span className="k">Total pagado</span>
                <span className="v">{pesos(datos.total)}</span>
              </p>
            </div>

            {datos.comprobante !== null ? <Comprobante comprobante={datos.comprobante} /> : null}

            <div className="confirmacion-acciones">
              <Link to={rutas.inicio()} className="btn btn-lg btn-primario">
                Seguir comprando
              </Link>
            </div>

            <p className="confirmacion-ayuda">
              Guarda el número de pedido como respaldo. ¿Alguna duda? Escríbenos por{' '}
              <a
                href={whatsappConsultaPedido(datos.referencia)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => registrarClicWhatsapp({ origen: 'checkout' })}
              >
                WhatsApp
              </a>
              .
            </p>
          </section>
        ) : null}

        {datos !== null && !datos.aprobado ? (
          <section className="confirmacion confirmacion-fallo" aria-labelledby="confirmacion-titulo">
            <div className="confirmacion-insignia" aria-hidden="true">
              <div className="confirmacion-check">
                <Icono nombre="alerta" />
              </div>
            </div>

            <h1 id="confirmacion-titulo">Tu pago no se pudo procesar</h1>
            <p className="confirmacion-bajada">
              Tranquilo, no se realizó ningún cobro. Tu carro sigue completo: puedes intentarlo de nuevo o
              escribirnos y lo resolvemos juntos.
            </p>

            <div className="confirmacion-ticket">
              <p className="confirmacion-fila">
                <span className="k">Número de pedido</span>
                <span className="mono">{datos.referencia}</span>
              </p>
            </div>

            <div className="confirmacion-acciones">
              <Link to={rutas.checkout()} className="btn btn-lg btn-primario">
                Volver al checkout
              </Link>
              <a
                className="btn btn-lg btn-wa"
                href={whatsappPagoRechazado(datos.referencia, datos.total)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => registrarClicWhatsapp({ origen: 'checkout' })}
              >
                <Icono nombre="wa" />
                Escribir por WhatsApp
              </a>
            </div>
          </section>
        ) : null}
      </div>
    </>
  );
}
